import { graphqlPost } from "api/graphqlClient";
import { Object_Template } from "graphql/generated/graphql";
import { getSearchObj } from "utils/utils";
import { ScopeComparison } from "./relationGraphFilter";

export const TEMPLATES_PER_BATCH = 20;
/** Smaller batch when each template loads nested custom objects (GraphQL cost limit). */
export const TEMPLATES_WITH_OBJECTS_PER_BATCH = 8;
/** Max templates per objectTemplateGraphList query when fetching by id list. */
export const TEMPLATE_IDS_PER_GRAPH_QUERY = 10;
export const ATTRIBUTES_PER_BATCH = 10;
export const CUSTOM_OBJECTS_PER_BATCH = 30;
/**
 * Custom objects loaded per template container on the FIRST (initial) paint of
 * the interactive object canvas. The remainder loads on demand via the
 * per-container "load more" affordance, so initial paint is bounded by
 * (#templates x this) instead of the total number of custom objects in scope.
 * (operator-task-fe-canvas-progressive-loading-design)
 *
 * Lowered 25 -> 10 on 2026-09-01 (Margot, operator-task-fe-canvas-loading-perf-followup):
 * the object canvas was still slow to load with many objects, so the initial
 * paint is bounded more aggressively; the per-container "load more" affordance
 * still reveals the rest on demand.
 */
export const INITIAL_CUSTOM_OBJECTS_PER_TEMPLATE = 10;
/** Reference edges fetched per template in object-template graph mode. */
export const TEMPLATE_REFERENCE_LIMIT = 50;

/**
 * Deep-link neighborhood seed
 * (operator-task-fe-canvas-highlight-record-and-neighbors-on-link): when the
 * interactive object canvas is opened from a specific record (?focus=<id>), seed
 * the initial view with just that record + its immediate neighbors instead of the
 * whole container scope. "Immediate neighbors" = 1-hop references in BOTH
 * directions (incoming via `referencing_custom_objects`; outgoing resolved from
 * the focus record's OBJECT_TEMPLATE_REFERENCE / CUSTOM_OBJECT-reference attribute
 * values) PLUS a bounded set of same-template siblings and their relations. The
 * union is capped so the initial paint stays small.
 */
export const NEIGHBORHOOD_CAP = 60;
/** Same-template siblings pulled into the neighborhood seed (newest first). */
export const NEIGHBORHOOD_SIBLING_LIMIT = 25;

/** Drop nullish or id-less rows from GraphQL list responses before graph assembly. */
export function filterDefinedTemplates<T extends { id?: string | null }>(
    templates: Array<T | null | undefined> | null | undefined
): T[] {
    return (templates ?? []).filter((template): template is T => !!template?.id);
}

/** Fields for nested custom_object_list / template graph queries. */
const CUSTOM_OBJECT_SEED_FIELDS = `
    id
    name
    object_template_id
    provisioning_state
    value
    is_readonly
    deleted_at
    project_id
    bundle_id
    referencing_custom_objects
    project_id_project {
        id
        name
    }
    bundle_id_bundle {
        id
        name
    }
`;

/** Slimmer field set for custom_object_plain_list (partition_by seeds). */
const CUSTOM_OBJECT_PLAIN_SEED_FIELDS = `
    id
    name
    object_template_id
    provisioning_state
    is_readonly
    deleted_at
    bundle_id
    project_id
    referencing_custom_objects
`;

const CUSTOM_OBJECT_LIST_BATCH_QUERY = `query customObjectSeedList(
    $filter: String,
    $limit: Int,
    $offset: Int,
    $order: String
) {
    custom_object_list(filter: $filter, limit: $limit, offset: $offset, order: $order) {
        custom_object_count
        custom_object_list {
            ${CUSTOM_OBJECT_SEED_FIELDS}
        }
    }
}`;

const TEMPLATE_GRAPH_BATCH_QUERY = `query objectTemplateGraphList(
    $filter: String,
    $limit: Int,
    $offset: Int,
    $order: String,
    $attributeLimit: Int,
    $attributeOffset: Int,
    $attributeOrder: String,
    $customObjectLimit: Int,
    $customObjectOffset: Int,
    $referenceLimit: Int
) {
    object_template_list(filter: $filter, limit: $limit, offset: $offset, order: $order) {
        object_template_count
        object_template_list {
            id
            name
            project_id
            bundle_id
            deleted_at
            is_readonly
            object_template_attribute_plain_list_reference_object_template_id(
                limit: $referenceLimit,
                order: "+name",
                offset: 0
            ) {
                id
                object_template_id
            }
            object_template_attribute_list_object_template_id(
                limit: $attributeLimit,
                order: $attributeOrder,
                offset: $attributeOffset
            ) {
                object_template_attribute_count
                object_template_attribute_list {
                    id
                    name
                    object_template_id
                    datatype
                    reference_object_template_id
                    reference_record_type
                    is_nullable
                    is_unique
                    is_hidden
                    silent_update
                }
            }
            custom_object_list_object_template_id(
                limit: $customObjectLimit,
                offset: $customObjectOffset,
                order: "+name"
            ) {
                custom_object_count
                custom_object_list {
                    ${CUSTOM_OBJECT_SEED_FIELDS}
                }
            }
        }
    }
}`;

/**
 * Container-seed query: the first `partition_by.limit` custom objects per
 * distinct object_template in the container, in ONE query, carrying the FULL
 * seed field set (incl. `value`, so child cards render). With `partition_by`
 * the top-level `limit`/`offset` page over whole PARTITIONS (templates), not
 * rows, and `custom_object_count` reports the total distinct templates matching
 * the filter. (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
 */
const CONTAINER_CUSTOM_OBJECT_SEED_QUERY = `query containerCustomObjectSeed(
    $filter: String,
    $limit: Int,
    $offset: Int,
    $order: String,
    $partitionBy: PartitionByInput
) {
    custom_object_list(
        filter: $filter,
        limit: $limit,
        offset: $offset,
        order: $order,
        partition_by: $partitionBy
    ) {
        custom_object_count
        custom_object_list {
            ${CUSTOM_OBJECT_SEED_FIELDS}
        }
    }
}`;

const CUSTOM_OBJECT_PLAIN_LIST_QUERY = `query customObjectPlainSeedList(
    $filter: String,
    $limit: Int,
    $order: String,
    $partitionBy: PartitionByInput
) {
    custom_object_plain_list(
        filter: $filter,
        limit: $limit,
        order: $order,
        partition_by: $partitionBy
    ) {
        ${CUSTOM_OBJECT_PLAIN_SEED_FIELDS}
    }
}`;

export interface CustomObjectSeedPartitionBy {
    field: string;
    limit: number;
    order: string;
}

async function fetchPartitionedCustomObjectSeeds(
    filter: string | null,
    partitionBy: CustomObjectSeedPartitionBy,
    groupLimit: number,
    order: string
): Promise<any[]> {
    const response = (await graphqlPost({
        query: CUSTOM_OBJECT_PLAIN_LIST_QUERY,
        variables: {
            filter,
            limit: groupLimit,
            order,
            partitionBy: {
                field: partitionBy.field,
                limit: partitionBy.limit,
                order: partitionBy.order,
            },
        },
        allow_normal: true,
        allow_deleted: true,
    })) as any;

    const data = response?.data?.custom_object_plain_list;
    return Array.isArray(data) ? data.filter((item) => item?.id) : [];
}

async function fetchAllCustomObjectSeeds(
    filter: string | null,
    shouldContinue?: () => boolean
): Promise<any[]> {
    const items: any[] = [];
    let offset = 0;

    while (true) {
        // Stop paging early if the caller (e.g. an unmounted embed canvas) is no
        // longer interested — avoids issuing the rest of the pages for nothing.
        if (shouldContinue && !shouldContinue()) {
            break;
        }
        const response = (await graphqlPost({
            query: CUSTOM_OBJECT_LIST_BATCH_QUERY,
            variables: {
                filter,
                limit: CUSTOM_OBJECTS_PER_BATCH,
                offset,
                order: "+name",
            },
            allow_normal: true,
            allow_deleted: true,
        })) as any;

        const batch =
            response?.data?.custom_object_list?.custom_object_list ?? [];
        const count = response?.data?.custom_object_list?.custom_object_count ?? 0;
        items.push(...batch.filter((item) => item?.id));
        offset += batch.length;
        if (offset >= count || batch.length === 0) {
            break;
        }
    }

    return items;
}

/** Distinct OWN object_template ids of the given custom objects (ignores referencing templates). */
function collectOwnTemplateIds(customObjects: any[]): string[] {
    const templateIds = new Set<string>();
    customObjects.forEach((customObject) => {
        if (customObject?.object_template_id) {
            templateIds.add(customObject.object_template_id);
        }
    });
    return Array.from(templateIds);
}

function collectTemplateIdsFromCustomObjects(customObjects: any[]): string[] {
    const templateIds = new Set<string>();
    customObjects.forEach((customObject) => {
        if (customObject?.object_template_id) {
            templateIds.add(customObject.object_template_id);
        }
        customObject?.referencing_custom_objects?.forEach((reference: any) => {
            if (reference?.object_template_id) {
                templateIds.add(reference.object_template_id);
            }
        });
    });
    return Array.from(templateIds);
}

function mergeCustomObjectsIntoTemplates(
    templates: Object_Template[],
    customObjects: any[]
): Object_Template[] {
    const definedTemplates = filterDefinedTemplates(templates);
    const objectsByTemplateId = customObjects.reduce<Record<string, any[]>>((acc, customObject) => {
        const templateId = customObject?.object_template_id;
        if (!templateId) {
            return acc;
        }
        acc[templateId] = [...(acc[templateId] ?? []), customObject];
        return acc;
    }, {});

    const mergedTemplates = definedTemplates.map((template) => {
        const seededObjects = objectsByTemplateId[template.id] ?? [];
        const existingObjects =
            template.custom_object_list_object_template_id?.custom_object_list ?? [];
        const objectById = new Map<string, any>();
        [...existingObjects, ...seededObjects].forEach((customObject) => {
            if (customObject?.id) {
                objectById.set(customObject.id, customObject);
            }
        });

        return {
            ...template,
            custom_object_list_object_template_id: {
                custom_object_count: objectById.size,
                custom_object_list: Array.from(objectById.values()),
            },
        };
    });

    const knownTemplateIds = new Set(mergedTemplates.map((template) => template.id));
    Object.entries(objectsByTemplateId).forEach(([templateId, seededObjects]) => {
        if (knownTemplateIds.has(templateId)) {
            return;
        }
        mergedTemplates.push({
            id: templateId,
            name: seededObjects[0]?.name ?? templateId,
            custom_object_list_object_template_id: {
                custom_object_count: seededObjects.length,
                custom_object_list: seededObjects,
            },
        } as Object_Template);
    });

    return mergedTemplates;
}

export async function fetchTemplateBatch(
    filter: string | null,
    offset: number,
    options: {
        attributeLimit: number;
        customObjectLimit: number;
        referenceLimit: number;
        templateLimit?: number;
    }
): Promise<{ items: Object_Template[]; count: number }> {
    const response = (await graphqlPost({
        query: TEMPLATE_GRAPH_BATCH_QUERY,
        variables: {
            filter,
            limit: options.templateLimit ?? TEMPLATES_PER_BATCH,
            offset,
            order: "+name",
            attributeLimit: options.attributeLimit,
            attributeOffset: 0,
            attributeOrder: "+name",
            customObjectLimit: options.customObjectLimit,
            customObjectOffset: 0,
            referenceLimit: options.referenceLimit,
        },
        allow_normal: true,
        allow_deleted: true,
    })) as any;

    return {
        items: filterDefinedTemplates(
            response?.data?.object_template_list?.object_template_list
        ),
        count: response?.data?.object_template_list?.object_template_count ?? 0,
    };
}

export async function fetchTemplatesForCustomObjects(
    idList: string[],
    offset: number,
    customObjectLimit: number
): Promise<Object_Template[]> {
    if (idList.length === 0) {
        return [];
    }

    const mergedTemplates: Object_Template[] = [];

    for (let batchOffset = 0; batchOffset < idList.length; batchOffset += TEMPLATE_IDS_PER_GRAPH_QUERY) {
        const idBatch = idList.slice(batchOffset, batchOffset + TEMPLATE_IDS_PER_GRAPH_QUERY);
        try {
            const response = (await graphqlPost({
                query: TEMPLATE_GRAPH_BATCH_QUERY,
                variables: {
                    filter: JSON.stringify(
                        getSearchObj({ field: "id", op: "in", value: idBatch })
                    ),
                    limit: idBatch.length,
                    offset: 0,
                    order: "+name",
                    attributeLimit: 0,
                    attributeOffset: 0,
                    attributeOrder: "+name",
                    customObjectLimit,
                    customObjectOffset: offset,
                    referenceLimit: 0,
                },
                allow_normal: true,
                allow_deleted: true,
            })) as any;

            mergedTemplates.push(
                ...filterDefinedTemplates(
                    response?.data?.object_template_list?.object_template_list
                )
            );
        } catch {
            // Skip failed batches; partial graph data is better than none.
        }
    }

    return filterDefinedTemplates(mergedTemplates);
}

export async function recursiveCustomObjectFetch(
    objectTemplateList: Object_Template[],
    offset: number,
    customObjectsPerBatch: number,
    shouldContinue?: () => boolean
): Promise<Object_Template[]> {
    const definedTemplates = filterDefinedTemplates(objectTemplateList);
    // Stop recursing (paging further pages) if the caller is no longer interested
    // (e.g. an embed canvas unmounted mid-load).
    if (shouldContinue && !shouldContinue()) {
        return definedTemplates;
    }
    const idList: string[] = [];

    definedTemplates.forEach((template) => {
        const totalCount = template.custom_object_list_object_template_id?.custom_object_count || 0;
        const currentCount =
            template.custom_object_list_object_template_id?.custom_object_list?.length || 0;
        if (currentCount < totalCount) {
            idList.push(template.id);
        }
    });

    if (idList.length === 0) {
        return definedTemplates;
    }

    const newData = await fetchTemplatesForCustomObjects(
        idList,
        offset,
        customObjectsPerBatch
    );
    if (shouldContinue && !shouldContinue()) {
        return definedTemplates;
    }
    const updatedObjects = definedTemplates.map((template) => ({
        ...template,
        custom_object_list_object_template_id: {
            ...template.custom_object_list_object_template_id,
            custom_object_list: [
                ...(template.custom_object_list_object_template_id?.custom_object_list || []),
                ...(newData?.find((item) => item.id === template.id)
                    ?.custom_object_list_object_template_id?.custom_object_list || []),
            ],
        },
    }));

    return recursiveCustomObjectFetch(
        updatedObjects,
        offset + customObjectsPerBatch,
        customObjectsPerBatch,
        shouldContinue
    );
}

/**
 * Fetch object templates (and nested custom objects) for a custom-object relation graph.
 *
 * When `customObjectFilter` is set, seeds the graph from matching custom objects and then
 * loads the templates those objects belong to (plus referenced templates).
 * With `partitionBy`, uses `custom_object_plain_list` + `partition_by` (latest N per group).
 */
export async function fetchCustomObjectGraphTemplates(
    templateFilter: string | null,
    customObjectFilter: string | null,
    options?: {
        partitionBy?: CustomObjectSeedPartitionBy;
        groupLimit?: number;
        order?: string;
        /**
         * Optional abort predicate: returns false once the caller is no longer
         * interested (e.g. an embed canvas unmounted mid-load), so the paging
         * loops stop issuing further requests instead of exhausting the scope.
         */
        shouldContinue?: () => boolean;
    }
): Promise<Object_Template[]> {
    const shouldContinue = options?.shouldContinue;
    if (customObjectFilter || options?.partitionBy) {
        const seedObjects = options?.partitionBy
            ? await fetchPartitionedCustomObjectSeeds(
                  customObjectFilter,
                  options.partitionBy,
                  options.groupLimit ?? 50,
                  options.order ?? "-created_at"
              )
            : await fetchAllCustomObjectSeeds(customObjectFilter, shouldContinue);
        if (shouldContinue && !shouldContinue()) {
            return [];
        }
        const templateIds = collectTemplateIdsFromCustomObjects(seedObjects);
        if (templateIds.length === 0) {
            return [];
        }

        const mergedList = await fetchTemplatesForCustomObjects(templateIds, 0, 0);
        return mergeCustomObjectsIntoTemplates(mergedList, seedObjects);
    }

    let offset = 0;
    let mergedList: Object_Template[] = [];

    while (true) {
        if (shouldContinue && !shouldContinue()) {
            break;
        }
        const { items, count } = await fetchTemplateBatch(templateFilter, offset, {
            attributeLimit: 0,
            customObjectLimit: CUSTOM_OBJECTS_PER_BATCH,
            referenceLimit: 0,
            templateLimit: TEMPLATES_WITH_OBJECTS_PER_BATCH,
        });
        const itemsWithObjects = await recursiveCustomObjectFetch(
            items,
            CUSTOM_OBJECTS_PER_BATCH,
            // customObjectsPerBatch left undefined to preserve prior runtime
            // behaviour (this call historically passed only two positional args);
            // only the abort predicate is threaded through here.
            undefined as unknown as number,
            shouldContinue
        );
        mergedList = offset === 0 ? itemsWithObjects : [...mergedList, ...itemsWithObjects];
        offset += items.length;
        if (offset >= count || items.length === 0) {
            break;
        }
    }

    return mergedList;
}

/**
 * Fetch all object templates for the object-template relation graph, batched.
 */
export async function fetchObjectTemplateGraphList(
    filter: string | null,
    options?: { shouldContinue?: () => boolean }
): Promise<Object_Template[]> {
    const shouldContinue = options?.shouldContinue;
    let offset = 0;
    let mergedList: Object_Template[] = [];

    while (true) {
        if (shouldContinue && !shouldContinue()) {
            break;
        }
        const { items, count } = await fetchTemplateBatch(filter, offset, {
            attributeLimit: ATTRIBUTES_PER_BATCH,
            customObjectLimit: 0,
            referenceLimit: TEMPLATE_REFERENCE_LIMIT,
        });
        mergedList = offset === 0 ? items : [...mergedList, ...items];
        offset += items.length;
        if (offset >= count || items.length === 0) {
            break;
        }
    }

    return mergedList;
}

/**
 * Resolve the custom-object ids the focus record REFERENCES (outgoing 1-hop).
 *
 * Incoming references arrive for free via `referencing_custom_objects` (a reverse
 * index), but there is no GraphQL field for outgoing ones, so they are read from
 * the focus record's stored `value` against its template's reference attributes
 * (OBJECT_TEMPLATE_REFERENCE, or RECORD_REFERENCE whose reference_record_type is
 * CUSTOM_OBJECT). Best-effort: any failure yields none, leaving incoming + sibling
 * neighbors intact.
 */
async function collectOutgoingReferenceIds(focus: any): Promise<string[]> {
    const focusTemplateId = focus?.object_template_id;
    if (!focusTemplateId) {
        return [];
    }
    let attributes: any[] = [];
    try {
        const { items } = await fetchTemplateBatch(
            JSON.stringify(getSearchObj({ field: "id", op: "eq", value: focusTemplateId })),
            0,
            { attributeLimit: 200, customObjectLimit: 0, referenceLimit: 0, templateLimit: 1 }
        );
        attributes =
            (items?.[0] as any)?.object_template_attribute_list_object_template_id
                ?.object_template_attribute_list ?? [];
    } catch {
        return [];
    }
    const value = (focus?.value ?? {}) as Record<string, any>;
    const ids: string[] = [];
    attributes.forEach((attribute: any) => {
        const isCustomObjectReference =
            attribute?.datatype === "OBJECT_TEMPLATE_REFERENCE" ||
            attribute?.reference_record_type === "CUSTOM_OBJECT";
        if (!isCustomObjectReference || !attribute?.name) {
            return;
        }
        const raw = value[attribute.name];
        if (typeof raw === "string" && raw) {
            ids.push(raw);
        } else if (Array.isArray(raw)) {
            raw.forEach((entry) => {
                if (typeof entry === "string" && entry) {
                    ids.push(entry);
                }
            });
        }
    });
    return ids;
}

/**
 * Fetch a bounded "focus + immediate neighbors" seed for the interactive object
 * canvas when it is deep-linked with ?focus=<customObjectId>.
 *
 * Returns an Object_Template[] shaped exactly like the merged list the eager path
 * builds, so it flows through buildGraph / getPositionedCustomObjectGraphList
 * unchanged. Reuses the existing seed -> templates -> merge pipeline
 * (fetchCustomObjectGraphTemplates with an id-in custom-object filter), which
 * already collects the neighbors' templates (incl. referenced ones) and renders
 * incoming reference edges from each seed's `referencing_custom_objects`.
 * (operator-task-fe-canvas-highlight-record-and-neighbors-on-link)
 */
export async function fetchCustomObjectNeighborhood(
    focusId: string,
    cap: number = NEIGHBORHOOD_CAP,
    onMeta?: (meta: { neighborhoodTotal: number }) => void
): Promise<Object_Template[]> {
    if (!focusId) {
        return [];
    }

    // 1. Fetch the focus record itself (value + incoming references).
    const focusResponse = (await graphqlPost({
        query: CUSTOM_OBJECT_LIST_BATCH_QUERY,
        variables: {
            filter: JSON.stringify(getSearchObj({ field: "id", op: "eq", value: focusId })),
            limit: 1,
            offset: 0,
            order: "+name",
        },
        allow_normal: true,
        allow_deleted: true,
    })) as any;
    const focus = focusResponse?.data?.custom_object_list?.custom_object_list?.[0];
    if (!focus?.id) {
        return [];
    }
    const focusTemplateId = focus.object_template_id;

    const neighborIds = new Set<string>();

    // Incoming neighbors: objects that reference the focus (reverse index).
    (focus.referencing_custom_objects ?? []).forEach((reference: any) => {
        if (reference?.id) {
            neighborIds.add(reference.id);
        }
    });

    // Outgoing neighbors: objects the focus references (resolved from its value).
    (await collectOutgoingReferenceIds(focus)).forEach((id) => neighborIds.add(id));

    // Same-template siblings (newest first) + their own relations.
    if (focusTemplateId) {
        try {
            const siblingResponse = (await graphqlPost({
                query: CUSTOM_OBJECT_LIST_BATCH_QUERY,
                variables: {
                    filter: JSON.stringify(
                        getSearchObj({
                            field: "object_template_id",
                            op: "eq",
                            value: focusTemplateId,
                        })
                    ),
                    limit: NEIGHBORHOOD_SIBLING_LIMIT,
                    offset: 0,
                    order: "-created_at",
                },
                allow_normal: true,
                allow_deleted: true,
            })) as any;
            (siblingResponse?.data?.custom_object_list?.custom_object_list ?? []).forEach(
                (sibling: any) => {
                    if (sibling?.id) {
                        neighborIds.add(sibling.id);
                    }
                }
            );
        } catch {
            // Best-effort: siblings are additive.
        }
    }

    neighborIds.delete(focusId);

    // Size of the focus neighborhood (focus + all 1-hop refs + siblings) BEFORE the
    // display cap is applied, so callers can tell how many neighbors are still
    // hidden and offer a "load more" affordance.
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency)
    onMeta?.({ neighborhoodTotal: neighborIds.size + 1 });

    // Cap the neighborhood, always keeping the focus record itself.
    const cappedNeighborIds = Array.from(neighborIds).slice(0, Math.max(0, cap - 1));
    const seedIds = [focusId, ...cappedNeighborIds];

    const seedFilter = JSON.stringify(
        getSearchObj({ field: "id", op: "in", value: seedIds })
    );
    const merged = await fetchCustomObjectGraphTemplates(null, seedFilter);

    // Give each template its TRUE total custom-object count (not just the count of
    // objects that happened to land in the neighborhood), so the partial/focus view
    // can show the per-template "showing n of m" + Load more / Load all controls at
    // parity with the normal (non-focused) canvas.
    // (operator-task-fe-canvas-partial-load-hidden-objects-transparency: Margot (a)+(d))
    const templateIds = merged.map((template) => template.id).filter(Boolean) as string[];
    const trueCounts = await fetchTemplateObjectCounts(templateIds);
    return merged.map((template) => {
        const trueCount = trueCounts[template.id];
        if (trueCount === undefined) {
            return template;
        }
        return {
            ...template,
            custom_object_list_object_template_id: {
                ...template.custom_object_list_object_template_id,
                custom_object_count: trueCount,
            },
        };
    });
}

/**
 * Fetch each template's TRUE total custom-object count — the same
 * `custom_object_list_object_template_id.custom_object_count` the normal canvas
 * and the per-container "load more" rely on — so the focus/partial view can offer
 * per-template "showing n of m" + Load more / Load all controls consistent with
 * both. Count-only (customObjectLimit: 0 → no rows transferred), batched by
 * TEMPLATE_IDS_PER_GRAPH_QUERY. Best-effort: a template with no returned count is
 * simply omitted (the caller keeps its loaded count).
 * (operator-task-fe-canvas-partial-load-hidden-objects-transparency)
 */
export async function fetchTemplateObjectCounts(
    templateIds: string[]
): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};
    for (
        let offset = 0;
        offset < templateIds.length;
        offset += TEMPLATE_IDS_PER_GRAPH_QUERY
    ) {
        const idBatch = templateIds.slice(offset, offset + TEMPLATE_IDS_PER_GRAPH_QUERY);
        if (idBatch.length === 0) {
            continue;
        }
        try {
            const response = (await graphqlPost({
                query: `query templateObjectCounts($filter: String, $limit: Int) {
    object_template_list(filter: $filter, limit: $limit, offset: 0, order: "+name") {
        object_template_list {
            id
            custom_object_list_object_template_id(limit: 0, offset: 0, order: "+name") {
                custom_object_count
            }
        }
    }
}`,
                variables: {
                    filter: JSON.stringify(
                        getSearchObj({ field: "id", op: "in", value: idBatch })
                    ),
                    limit: idBatch.length,
                },
                allow_normal: true,
                allow_deleted: true,
            })) as any;
            (response?.data?.object_template_list?.object_template_list ?? []).forEach(
                (template: any) => {
                    if (template?.id) {
                        counts[template.id] =
                            template?.custom_object_list_object_template_id
                                ?.custom_object_count ?? 0;
                    }
                }
            );
        } catch {
            // Best-effort: leave this batch's templates showing their loaded count.
        }
    }
    return counts;
}

/**
 * Count all custom objects in scope with a count-only query (limit: 0 → no rows
 * transferred), so the focus/partial canvas can show a precise "showing N of M"
 * total without loading every object. A null filter counts the whole workspace —
 * exactly the scope the eager "Load all" path renders.
 * (operator-task-fe-canvas-partial-load-hidden-objects-transparency)
 */
export async function fetchCustomObjectTotalCount(
    filter: string | null
): Promise<number> {
    const response = (await graphqlPost({
        query: `query customObjectTotalCount($filter: String) {
    custom_object_list(filter: $filter, limit: 0) {
        custom_object_count
    }
}`,
        variables: { filter },
        allow_normal: true,
        allow_deleted: true,
    })) as any;
    return response?.data?.custom_object_list?.custom_object_count ?? 0;
}

/**
 * Distinct object_template partitions fetched per container-seed query. With
 * partition_by the top-level `limit` pages over whole partitions (templates),
 * so this bounds how many distinct templates a single round trip covers; the
 * loop below pages further if the container has more.
 * (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
 */
export const CONTAINER_TEMPLATE_PARTITIONS_PER_BATCH = 50;
/** Concurrency for the per-template container-scoped count queries. */
const CONTAINER_COUNT_CONCURRENCY = 6;

/** AND-combine the container scope with a single object_template_id comparison. */
function templateInContainerFilter(
    scopeFilter: ScopeComparison,
    templateId: string
): string {
    return JSON.stringify({
        and: [scopeFilter, { field: "object_template_id", op: "eq", value: templateId }],
    });
}

/**
 * True container-scoped total custom-object count for each template (count-only,
 * limit: 0 → no rows transferred), so per-container "showing n of m" + Load more
 * reflect the objects IN THIS CONTAINER rather than the template's workspace-wide
 * total. Only queried for templates that filled the initial per-template page
 * (others are already fully loaded). Best-effort: a failed/omitted template keeps
 * its loaded count. (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
 */
export async function fetchContainerTemplateObjectCounts(
    scopeFilter: ScopeComparison,
    templateIds: string[]
): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};
    for (let i = 0; i < templateIds.length; i += CONTAINER_COUNT_CONCURRENCY) {
        const chunk = templateIds.slice(i, i + CONTAINER_COUNT_CONCURRENCY);
        const results = await Promise.all(
            chunk.map(async (templateId): Promise<[string, number | undefined]> => {
                try {
                    const response = (await graphqlPost({
                        query: `query containerTemplateObjectCount($filter: String) {
    custom_object_list(filter: $filter, limit: 0) {
        custom_object_count
    }
}`,
                        variables: { filter: templateInContainerFilter(scopeFilter, templateId) },
                        allow_normal: true,
                        allow_deleted: true,
                    })) as any;
                    return [
                        templateId,
                        response?.data?.custom_object_list?.custom_object_count ?? undefined,
                    ];
                } catch {
                    return [templateId, undefined];
                }
            })
        );
        results.forEach(([templateId, count]) => {
            if (count !== undefined) {
                counts[templateId] = count;
            }
        });
    }
    return counts;
}

/**
 * Fetch one container-scoped page of a single template's custom objects, shaped
 * as an Object_Template[] (so the canvas' existing per-container "load more"
 * merge path consumes it unchanged). Used for the CONTAINER canvas so paging a
 * foreign template (defined in another container) stays within THIS container
 * instead of pulling in that template's objects from everywhere.
 * (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
 */
export async function fetchContainerTemplateCustomObjects(
    scopeFilter: ScopeComparison,
    templateId: string,
    offset: number,
    limit: number
): Promise<Object_Template[]> {
    const response = (await graphqlPost({
        query: CONTAINER_CUSTOM_OBJECT_SEED_QUERY,
        variables: {
            filter: templateInContainerFilter(scopeFilter, templateId),
            limit,
            offset,
            order: "+name",
            partitionBy: null,
        },
        allow_normal: true,
        allow_deleted: true,
    })) as any;
    const rows = response?.data?.custom_object_list?.custom_object_list ?? [];
    const count = response?.data?.custom_object_list?.custom_object_count ?? rows.length;
    return [
        {
            id: templateId,
            custom_object_list_object_template_id: {
                custom_object_count: count,
                custom_object_list: rows.filter((row: any) => row?.id),
            },
        } as Object_Template,
    ];
}

/**
 * Build the custom-object relation graph for a project/bundle CONTAINER, scoped
 * by the custom objects that ACTUALLY LIVE in the container (by bundle_id/
 * project_id), NOT by which object_templates are defined there.
 *
 * This fixes the empty-canvas bug for a container that holds many custom objects
 * but defines no object_templates of its own (e.g. Agent_ops_content, whose
 * templates live in Agent_ops): the old template-enumeration path returned zero
 * templates → empty graph + a wrong "no objects" note. It also fixes the subtler
 * case of a container that defines SOME templates yet also holds objects of
 * foreign templates (the old path hid those).
 *
 * Bounded initial paint (preserves progressive loading): the seed loads at most
 * `perTemplateLimit` objects per distinct template via partition_by, and each
 * template's true container-scoped total is resolved count-only so the
 * per-container "load more" affordance stays correct. The remainder loads on
 * demand (container-scoped — see fetchContainerTemplateCustomObjects).
 * (operator-task-fe-canvas-empty-when-container-has-objects-no-templates)
 */
export async function fetchContainerCustomObjectGraph(
    scopeFilter: ScopeComparison,
    perTemplateLimit: number,
    options?: { shouldContinue?: () => boolean }
): Promise<Object_Template[]> {
    const shouldContinue = options?.shouldContinue;
    const scopeFilterStr = JSON.stringify(scopeFilter);

    // 1. Seed: top `perTemplateLimit` objects per distinct template in the
    //    container, paging over whole partitions (templates) if there are many.
    const seedObjects: any[] = [];
    const seenTemplateIds = new Set<string>();
    let partitionOffset = 0;
    while (true) {
        if (shouldContinue && !shouldContinue()) {
            return [];
        }
        const response = (await graphqlPost({
            query: CONTAINER_CUSTOM_OBJECT_SEED_QUERY,
            variables: {
                filter: scopeFilterStr,
                limit: CONTAINER_TEMPLATE_PARTITIONS_PER_BATCH,
                offset: partitionOffset,
                order: "+name",
                partitionBy: {
                    field: "object_template_id",
                    limit: perTemplateLimit,
                    order: "+name",
                },
            },
            allow_normal: true,
            allow_deleted: true,
        })) as any;

        const batch = response?.data?.custom_object_list?.custom_object_list ?? [];
        const distinctTemplateCount =
            response?.data?.custom_object_list?.custom_object_count ?? 0;
        const batchTemplateIds = new Set<string>();
        batch.forEach((customObject: any) => {
            if (customObject?.id) {
                seedObjects.push(customObject);
                if (customObject.object_template_id) {
                    batchTemplateIds.add(customObject.object_template_id);
                }
            }
        });
        batchTemplateIds.forEach((id) => seenTemplateIds.add(id));
        // limit/offset page over whole partitions → advance by the distinct
        // templates covered in this batch.
        partitionOffset += batchTemplateIds.size;
        if (
            batch.length === 0 ||
            batchTemplateIds.size === 0 ||
            seenTemplateIds.size >= distinctTemplateCount
        ) {
            break;
        }
    }

    if (seedObjects.length === 0) {
        return [];
    }

    // 2. Real template metadata (names + project_id/bundle_id so the graph
    //    builder can classify in-container vs foreign templates). Count-only.
    const templateIds = collectOwnTemplateIds(seedObjects);
    if (shouldContinue && !shouldContinue()) {
        return [];
    }
    const templateMeta = await fetchTemplatesForCustomObjects(templateIds, 0, 0);

    // 3. Merge the container's seed objects into their templates.
    const merged = mergeCustomObjectsIntoTemplates(templateMeta, seedObjects);

    // 4. Give templates that filled their initial page a TRUE container-scoped
    //    count so "load more" appears when (and only when) more objects exist.
    const fullTemplateIds = merged
        .filter(
            (template) =>
                (template.custom_object_list_object_template_id?.custom_object_list
                    ?.length ?? 0) >= perTemplateLimit
        )
        .map((template) => template.id)
        .filter((id): id is string => !!id);
    if (shouldContinue && !shouldContinue()) {
        return [];
    }
    const trueCounts = await fetchContainerTemplateObjectCounts(scopeFilter, fullTemplateIds);
    return merged.map((template) => {
        const loaded =
            template.custom_object_list_object_template_id?.custom_object_list?.length ?? 0;
        const trueCount = trueCounts[template.id];
        return {
            ...template,
            custom_object_list_object_template_id: {
                ...template.custom_object_list_object_template_id,
                custom_object_count: trueCount ?? loaded,
            },
        };
    });
}

/**
 * "N objects" label for a template node. `undefined` means the count has not
 * arrived yet: the object-template canvas paints its nodes first and loads the
 * counts in a second pass (operator-task-fe-object-canvas-slow-graphql-pages).
 */
export const formatObjectCount = (count: number | undefined): string => {
    if (count === undefined || count === null) {
        return "… objects";
    }
    return `${count} ${count === 1 ? "object" : "objects"}`;
};
